"use client";

import { NextStep, useNextStep } from "nextstepjs";
import mainGuide from "@/guide/mainGuide";
import { usePathname, useSearchParams } from "next/navigation";

import {
  useAppSelector,
  useEffect,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import CustomGuideCard from "./guides/GuideCard";
import manageListGuide from "@/guide/guides/manageListGuide";
import dashboardGuide from "./guides/dashboardGuide";
import flyerGuide from "./guides/flyerGuide";
import recipeListGuide from "./guides/recipeListGuide";
import recipeDeatailGuide from "./guides/recipeDeatailGuide";
import offersGuide from "./guides/offersGuide";
import recipesGuide from "./guides/recipesGuide";
import searchGuide from "./guides/searchGuide";
import productGuide from "./guides/productGuide";
import manageListFilterGuide from "./guides/manageFiters";

const guidePaths: { path: string; exact: boolean }[] = [
  { path: "/manage-list", exact: false },
  { path: "/flyer", exact: true },
  { path: "/recipes/my-recipe-lists", exact: true },
  { path: "/offers", exact: true },
  { path: "/recipes", exact: false }, // exact only
  { path: "/products", exact: false }, // allow /products/*
  { path: "/", exact: true },
];

export default function TourManager({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [filteredSteps, setFilteredSteps] = useState(mainGuide);
  const { startNextStep } = useNextStep();
  const { loggedIn: userLoggedIn } = useAppSelector(
    (state) => state.gigyaslice
  );

  // initializing OneTrust consent handling  (for delay  to load tour manager)
  const [oneTrustConsent, setOneTrustConsent] = useState<boolean>(false);
  function initConsentHandling() {
    if (typeof (window as any).OneTrust !== "undefined") {
      (window as any).OneTrust.OnConsentChanged(function () {
        if ((window as any)?.OnetrustActiveGroups?.includes("C0002")) {
          setOneTrustConsent(true);
        }
      });

      if ((window as any)?.OnetrustActiveGroups?.includes("C0002")) {
        setOneTrustConsent(true);
      }
    }
  }
  (window as any).OptanonWrapper = function () {
    initConsentHandling();
  }; //end

  const getCompletedTours = (): string[] => {
    try {
      return JSON.parse(localStorage.getItem("completed_tours") || "[]");
    } catch {
      return [];
    }
  };

  const markTourComplete = (tourId: string) => {
    const completed = getCompletedTours();
    if (!completed.includes(tourId)) {
      localStorage.setItem(
        "completed_tours",
        JSON.stringify([...completed, tourId])
      );
    }
  };

  const isVisible = (el: HTMLElement) =>
    !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

  const filterStepsBySelector = (guide: any[]): any[] => {
    const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;

    return guide.map((tour) => ({
      ...tour,
      steps: tour.steps.filter((step: any) => {
        if (!step.selector) return true;
        const el = document.querySelector(step.selector) as HTMLElement;
        return el && isVisible(el) && (!isMobile || !step?.hideOnMobile);
      }),
    }));
  };

  const initializeTour = (
    guide: any[],
    id: string,
    retryCount = 0,
    maxRetries = 10
  ) => {
    const filtered = filterStepsBySelector(guide);
    const hasSteps = filtered.some((g) => g.steps.length > 0);
    const alreadyCompleted = getCompletedTours().includes(id);

    if (hasSteps && !alreadyCompleted) {
      document.body.classList.add("custom-guide-active");
      window.dispatchEvent(new Event("guide-activated"));
      setFilteredSteps(filtered);
      startNextStep(id);
    } else {
      document.body.classList.remove("custom-guide-active");
      if (retryCount < maxRetries) {
        setTimeout(() => initializeTour(guide, id, retryCount + 1), 500);
      } else {
        console.warn(`${id}: No valid selectors found after max retries.`);
      }
    }
  };

  const retryRecipesTour = (pathname: string) => {
    let retryCount = 0;
    const maxRetries = 10;

    const tryStart = () => {
      const favoriteElement = document.getElementById("recipe_favorite_0");
      const detailElement = document.getElementById("create_new_recipeList");

      if (pathname === "/recipes" && favoriteElement) {
        initializeTour(recipesGuide, "recipesTour");
      } else if (pathname.startsWith("/recipes/") && detailElement) {
        initializeTour(recipeDeatailGuide, "recipeDetailTour");
      } else if (retryCount < maxRetries) {
        retryCount++;
        setTimeout(tryStart, 500);
      } else {
        console.warn("Tour elements not found after max retries.");
      }
    };

    tryStart();
  };

  useEffect(() => {
    if (oneTrustConsent) {
      document.body.classList.remove("custom-guide-active");
      const completedTours = getCompletedTours();
      const start = (guide: any[], id: string) => {
        const filtered = filterStepsBySelector(guide);
        const hasSteps = filtered.some((g) => g.steps.length > 0);
        const alreadyCompleted = completedTours.includes(id);
        if (hasSteps && !alreadyCompleted) {
          document.body.classList.add("custom-guide-active");
          window.dispatchEvent(new Event("guide-activated"));
          setFilteredSteps(filtered);
          startNextStep(id);
        } else {
          document.body.classList.remove("custom-guide-active");
        }
      };

      const timeoutId = setTimeout(() => {
        switch (true) {
          case pathname === "/" && window.location.href.includes("?query="):
            setTimeout(() => {
              start(searchGuide, "searchTour");
            }, 1500);
            break;
          case pathname === "/":
            start(dashboardGuide, "welcome_to_sobeys");
            break;
          case pathname === "/manage-list":
            start(manageListGuide, "manageList");
            break;

          case pathname.startsWith("/manage-list"):
            start(manageListFilterGuide, "manageListFilter");
            break;

          case pathname === "/flyer":
            start(flyerGuide, "flyerTour");
            break;

          case pathname === "/recipes/my-recipe-lists":
            start(recipeListGuide, "recipeListTour");
            break;

          case pathname === "/offers":
            start(offersGuide, "offersTour");
            break;

          case pathname.startsWith("/recipes"):
            setTimeout(() => {
              retryRecipesTour(pathname);
            }, 500);
            break;
        }
      }, 1500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams, userLoggedIn, oneTrustConsent]);

  const handleComplete = (tourName: string | null) => {
    //removing scroll class form body
    document.body.classList.remove("custom-guide-active");
    if (tourName) {
      markTourComplete(tourName);
    }
  };

  const handleSkip = (step: number, tourName: string | null) => {
    if (tourName) {
      markTourComplete(tourName);
    }
    //removing scroll class form body
    document.body.classList.remove("custom-guide-active");
  };

  const scrollElementToCenter = (selector: string) => {
    const tryScroll = (attempt = 0) => {
      const element = document.querySelector(selector) as HTMLElement;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      // If element is off-screen or not visible yet
      const notInView =
        rect.top >= window.innerHeight || rect.bottom <= 0 || rect.height === 0;

      if (notInView && attempt < 10) {
        // Try again after a short delay
        requestAnimationFrame(() => tryScroll(attempt + 1));
        return;
      }

      const elementTop = rect.top + window.pageYOffset;
      const centerOffset =
        elementTop - window.innerHeight / 1.6 + rect.height / 1.6;

      window.scrollTo({
        top: centerOffset,
        behavior: "smooth",
      });
    };

    tryScroll();
  };

  useEffect(() => {
    const originalScrollIntoView = Element.prototype.scrollIntoView;
    Element.prototype.scrollIntoView = function () {
      scrollElementToCenter(`#${this.id}`);
    };
    return () => {
      Element.prototype.scrollIntoView = originalScrollIntoView;
    };
  }, []);

  const shouldShowGuide = guidePaths.some(({ path, exact }) =>
    exact ? pathname === path : pathname.startsWith(path)
  );

  return shouldShowGuide && filteredSteps ? (
    <NextStep
      steps={filteredSteps}
      cardComponent={CustomGuideCard}
      onComplete={handleComplete}
      onSkip={handleSkip}
      scrollToTop={true}
      noInViewScroll={true}
      disableConsoleLogs
      onStepChange={(step: any) => {
        if (step?.selector) {
          scrollElementToCenter(step.selector);
        }
      }}
    >
      {children}
    </NextStep>
  ) : (
    <>{children}</>
  );
}
